import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import {
  esPuntualEnSerie,
  mediana as medianaRobusta,
  referenciaHabitual,
} from '#components/mobile/informes/estadisticaRobusta';

import type { CadenciaMeses, DatosCategoriaMes, Objetivo } from './objetivos';
import { proximoVencimiento } from './objetivos';
import { esPagada, PREFIJO_NO_PAGADA } from './pagadas';

/**
 * Pagos fijos (contrato común). Un pago fijo es una categoría con el
 * interruptor «Pago fijo» de su ficha (pref `pagos-fijos`). El importe y la
 * frecuencia de lo que se aparta salen del objetivo; cómo se cobra de
 * verdad (cada cuántos meses, importe típico), del historial o de la pref
 * `pagos-fijos-frecuencias`; el día de cargo, de la
 * marca `#objetivo día N` o, si no hay, del historial; la cuenta de cargo,
 * de la pref `pagos-fijos-cuentas` o, si no hay, del historial.
 * Funciones puras.
 */

export type EstadoPagoFijo =
  | 'pagado'
  | 'toca-hoy'
  | 'en-camino'
  | 'atrasado'
  | 'proximo'
  | 'programado';

/**
 * Días naturales de margen tras el día de cargo antes de «Atrasado»: los
 * pagos fijos son domiciliados y el cargo del banco puede tardar unos días
 * en llegar. Mientras tanto, «En camino».
 */
export const MARGEN_DIAS_PAGO_FIJO = 3;

/** Cada cuánto se aparta: sale del objetivo (ver `CobroReal` para el cargo). */
export type FrecuenciaPagoFijo =
  | { tipo: 'mensual' }
  | { tipo: 'cada-meses'; meses: CadenciaMeses }
  | { tipo: 'una-vez' };

export type PagoFijo = {
  /** Id de la categoría. */
  id: string;
  nombre: string;
  grupoId: string;
  grupoNombre: string;
  /** Importe del objetivo (céntimos, positivo); `null` sin objetivo. */
  importe: IntegerAmount | null;
  /** `null`: la categoría no tiene un objetivo mensual, anual ni «una vez». */
  frecuencia: FrecuenciaPagoFijo | null;
  /** Día de cargo (mensuales): el guardado o, si no hay, el del historial. */
  dia: number | null;
  /** ¿El día sale del historial (no lo ha fijado Vadym)? */
  diaInferido: boolean;
  /**
   * Fecha del cargo `YYYY-MM-DD`: la de este mes (mensuales) o el próximo
   * vencimiento (anuales y «una vez»). `null` si no se sabe el día.
   */
  fecha: string | null;
  /** Cuenta de cargo: la guardada o, si no hay, la más frecuente. */
  cuentaId: string | null;
  /** ¿La cuenta está guardada en `pagos-fijos-cuentas`? */
  cuentaGuardada: boolean;
  /**
   * «Todas las cuentas» elegida (ficha o «Desde:»): sin cuenta de cargo
   * (`cuentaId` null) y sin pregunta de cambio de cuenta.
   */
  todasLasCuentas?: boolean;
  /**
   * El último cargo (este mes o el anterior) salió de otra cuenta distinta
   * de la guardada y aún no se ha respondido: hay que preguntar.
   */
  cambioCuenta?: CambioCuenta | null;
  estado: EstadoPagoFijo;
  pagada: boolean;
  datos: DatosCategoriaMes | undefined;
  /**
   * Cómo se cobra de verdad (puede no coincidir con lo que se aparta: una
   * factura bimestral que se ahorra al mes). Ausente en datos antiguos.
   */
  cobro?: CobroReal;
  /** Cada cuántos meses se cobra (1 = cada mes); `null` en «una vez». */
  cadaMesesCobro?: number | null;
  /** ¿Toca cobro en el mes calculado? */
  tocaEsteMes?: boolean;
  /** Importe de cada cargo (céntimos, positivo): lo que sale del banco. */
  importeCargo?: IntegerAmount | null;
};

/** Movimiento del historial de una categoría (solo lo que hace falta). */
export type MovimientoHistorial = {
  /** Id del movimiento (para recordar «No» a un cargo concreto). */
  id?: string;
  date: string;
  amount: IntegerAmount;
  account: string | null;
};

function ultimoDiaDelMes(mes: string): number {
  const [anio, m] = mes.split('-').map(Number);
  return new Date(Date.UTC(anio, m, 0)).getUTCDate();
}

/** Fecha de cargo del día `dia` en `mes` (recortada al fin de mes). */
export function fechaCargoDelMes(dia: number, mes: string): string {
  const d = Math.min(Math.max(1, Math.round(dia)), ultimoDiaDelMes(mes));
  return `${mes}-${String(d).padStart(2, '0')}`;
}

/** Días entre dos fechas `YYYY-MM-DD` (b − a). */
export function diasEntre(a: string, b: string): number {
  const ms = Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/** Último día «En camino» de un cargo: `fecha` + margen (cruza de mes). */
export function finMargenPagoFijo(
  fecha: string,
  margen = MARGEN_DIAS_PAGO_FIJO,
): string {
  const d = new Date(Date.parse(`${fecha}T00:00:00Z`) + margen * 86_400_000);
  return d.toISOString().slice(0, 10);
}

/** Fase de un cargo no pagado según los días que lleva: hoy, en camino o atrasado. */
export function faseCargo(
  fecha: string,
  hoy: string,
  margen = MARGEN_DIAS_PAGO_FIJO,
): 'toca-hoy' | 'en-camino' | 'atrasado' | 'futuro' {
  const tarde = diasEntre(fecha, hoy);
  if (tarde < 0) return 'futuro';
  if (tarde === 0) return 'toca-hoy';
  return tarde <= margen ? 'en-camino' : 'atrasado';
}

/**
 * Estado de un pago en su fecha de cargo: pagado gana a todo; sin fecha es
 * `programado` (nunca un falso «Atrasado»); si es hoy, `toca-hoy`; los
 * `MARGEN_DIAS_PAGO_FIJO` días siguientes, `en-camino` (domiciliado: el
 * cargo puede tardar); después, `atrasado`; en ≤ `ventanaDias`, `proximo`;
 * más tarde, `programado`.
 */
export function estadoPagoFijo({
  fecha,
  hoy,
  pagada,
  ventanaDias = 7,
}: {
  fecha: string | null;
  hoy: string;
  pagada: boolean;
  ventanaDias?: number;
}): EstadoPagoFijo {
  if (pagada) {
    return 'pagado';
  }
  if (!fecha) {
    return 'programado';
  }
  const fase = faseCargo(fecha, hoy);
  if (fase !== 'futuro') {
    return fase;
  }
  return diasEntre(hoy, fecha) <= ventanaDias ? 'proximo' : 'programado';
}

/** Frecuencia que da el objetivo (o `null` si no sirve para un pago fijo). */
export function frecuenciaDeObjetivo(
  objetivo: Objetivo | null | 'otro' | undefined,
): FrecuenciaPagoFijo | null {
  if (!objetivo || objetivo === 'otro') {
    return null;
  }
  switch (objetivo.tipo) {
    case 'mensual':
      return { tipo: 'mensual' };
    case 'anual':
      return { tipo: 'cada-meses', meses: objetivo.cadaMeses };
    case 'una-vez':
      return { tipo: 'una-vez' };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Historial: día de cargo y cuenta
// ---------------------------------------------------------------------------

/** Cargos usados para inferir (los más recientes). */
export const CARGOS_HISTORIAL = 12;

/**
 * Un cargo por mes: la salida más grande de cada mes (así un reembolso o
 * un cargo suelto pequeño no mueve el día). Ordenados del más reciente al
 * más antiguo y limitados a `max`.
 */
export function cargosPorMes(
  movimientos: readonly MovimientoHistorial[],
  max = CARGOS_HISTORIAL,
): MovimientoHistorial[] {
  const porMes = new Map<string, MovimientoHistorial>();
  for (const m of movimientos) {
    if (m.amount >= 0) {
      continue;
    }
    const mes = m.date.slice(0, 7);
    const actual = porMes.get(mes);
    if (!actual || m.amount < actual.amount) {
      porMes.set(mes, m);
    }
  }
  return [...porMes.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .slice(0, max)
    .map(([, m]) => m);
}

function mediana(valores: number[]): number {
  const v = [...valores].sort((a, b) => a - b);
  const mitad = Math.floor(v.length / 2);
  return v.length % 2 ? v[mitad] : Math.round((v[mitad - 1] + v[mitad]) / 2);
}

/**
 * Día de cargo típico (mensuales): mediana del día del mes de los últimos
 * cargos (uno por mes, hasta 12). `null` sin historial.
 */
export function inferirDiaCargo(
  movimientos: readonly MovimientoHistorial[],
): number | null {
  const cargos = cargosPorMes(movimientos);
  if (cargos.length === 0) {
    return null;
  }
  return mediana(cargos.map(c => Number(c.date.slice(8, 10))));
}

// ---------------------------------------------------------------------------
// Frecuencia real del cargo («cómo se cobra», distinto de «lo que apartas»)
// ---------------------------------------------------------------------------

/** Frecuencias de cobro que se pueden elegir o inferir. */
export const MESES_COBRO = [1, 2, 3, 6, 12] as const;
export type MesesCobro = (typeof MESES_COBRO)[number];

/** Cargos mínimos para inferir la frecuencia. */
export const MIN_CARGOS_FRECUENCIA = 3;

export type CobroReal = {
  /** Cada cuántos meses; `'irregular'` si no hay patrón; `null` sin datos. */
  meses: MesesCobro | 'irregular' | null;
  /** De dónde sale `meses`. */
  origen: 'manual' | 'historial' | null;
  /**
   * Importe típico de cada recibo (céntimos, positivo): mediana de los
   * cargos habituales (sin gastos puntuales) o, con menos de 3, el del
   * objetivo por cargo.
   */
  importeTipico: IntegerAmount | null;
  /** ¿`importeTipico` sale del objetivo (poco historial) y no del historial? */
  importeDelObjetivo?: boolean;
  /** Mes (`YYYY-MM`) del último cargo real. */
  ultimoMes: string | null;
};

/** Meses entre dos `YYYY-MM` (b − a). */
export function mesesEntre(a: string, b: string): number {
  const [ya, ma] = a.split('-').map(Number);
  const [yb, mb] = b.split('-').map(Number);
  return (yb - ya) * 12 + (mb - ma);
}

function medianaExacta(valores: readonly number[]): number {
  const v = [...valores].sort((a, b) => a - b);
  const mitad = Math.floor(v.length / 2);
  return v.length % 2 ? v[mitad] : (v[mitad - 1] + v[mitad]) / 2;
}

/** Redondea una separación en meses a 1, 2, 3, 6 o 12. */
export function redondearMesesCobro(meses: number): MesesCobro {
  if (meses < 1.5) return 1;
  if (meses < 2.5) return 2;
  if (meses < 4.5) return 3;
  if (meses < 9) return 6;
  return 12;
}

/**
 * Cada cuántos meses se cobra según el historial: mediana de la separación
 * entre cargos consecutivos (uno por mes, el mayor; los últimos 12),
 * redondeada a 1, 2, 3, 6 o 12. Con menos de 3 cargos, `null`. Si menos de
 * la mitad de las separaciones se parecen a la mediana (± la mitad, mínimo
 * 1 mes), `'irregular'`.
 */
export function inferirFrecuenciaCargo(
  movimientos: readonly MovimientoHistorial[],
): MesesCobro | 'irregular' | null {
  const meses = cargosPorMes(movimientos).map(c => c.date.slice(0, 7));
  if (meses.length < MIN_CARGOS_FRECUENCIA) {
    return null;
  }
  const saltos: number[] = [];
  for (let i = 0; i + 1 < meses.length; i++) {
    saltos.push(mesesEntre(meses[i + 1], meses[i]));
  }
  const med = medianaExacta(saltos);
  const tolerancia = Math.max(1, med / 2);
  const parecidos = saltos.filter(s => Math.abs(s - med) <= tolerancia).length;
  if (parecidos * 2 < saltos.length) {
    return 'irregular';
  }
  return redondearMesesCobro(med);
}

/** Cargos habituales mínimos para fiarse del importe típico del historial. */
export const MIN_CARGOS_IMPORTE = 3;

/**
 * Cargos habituales, uno por mes (el mayor), sin gastos puntuales: de las
 * salidas de los meses con cargos (los últimos `max`), se quitan solo las
 * PUNTUALES (`esPuntualEnSerie`): lo habitual y su umbral robusto
 * (`referenciaHabitual`: mediana + max(3·MAD, 25 %, 10 €)) salen de esas
 * salidas recientes; para ver si un cargo alto se repite (mes contiguo, la
 * misma temporada de otro año u otro cargo ≥ 2 veces lo habitual) se mira
 * todo el historial que llega (hasta 36 meses). Así una entrada de 5.000 €
 * no tapa la cuota de 246,72 € del mismo mes, y un recibo variable o de
 * temporada (la luz de 40 a 70 €, el gas de invierno) cuenta.
 */
export function cargosHabitualesPorMes(
  movimientos: readonly MovimientoHistorial[],
  max = CARGOS_HISTORIAL,
): MovimientoHistorial[] {
  return cargosPorMes(salidasHabituales(movimientos, max), max);
}

/**
 * Lo habitual de cada mes, sumado (céntimos, positivo; del más reciente al
 * más antiguo): con dos recibos en la categoría (dos seguros), los dos.
 */
export function importesHabitualesPorMes(
  movimientos: readonly MovimientoHistorial[],
  max = CARGOS_HISTORIAL,
): number[] {
  const porMes = new Map<string, number>();
  for (const m of salidasHabituales(movimientos, max)) {
    const mes = m.date.slice(0, 7);
    porMes.set(mes, (porMes.get(mes) ?? 0) - m.amount);
  }
  return [...porMes.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .slice(0, max)
    .map(([, importe]) => importe);
}

/** Las salidas de los meses con cargos (los últimos `max`), sin puntuales. */
function salidasHabituales(
  movimientos: readonly MovimientoHistorial[],
  max: number,
): MovimientoHistorial[] {
  const meses = new Set(
    cargosPorMes(movimientos, max).map(c => c.date.slice(0, 7)),
  );
  const todas = movimientos.filter(m => m.amount < 0);
  const serie = todas.map(m => ({
    mes: m.date.slice(0, 7),
    importe: -m.amount,
  }));
  const enVentana = todas.map((m, i) => i).filter(i => meses.has(serie[i].mes));
  const ref = referenciaHabitual(enVentana.map(i => serie[i].importe));
  return enVentana
    .filter(i => !esPuntualEnSerie(i, serie, ref.mediana, ref.umbral))
    .map(i => todas[i]);
}

/**
 * Importe del objetivo por cargo: mensual × cada cuántos meses se cobra;
 * anual y «una vez», su importe. `null` sin objetivo utilizable.
 */
export function importeObjetivoPorCargo(
  objetivo: Objetivo | null | 'otro' | undefined,
  cadaMeses: number,
): IntegerAmount | null {
  const f = frecuenciaDeObjetivo(objetivo);
  if (!f || !objetivo || objetivo === 'otro') {
    return null;
  }
  return f.tipo === 'mensual'
    ? objetivo.importe * Math.max(1, cadaMeses)
    : objetivo.importe;
}

/**
 * Importe típico de cada recibo (positivo): mediana de lo habitual de cada
 * mes, sumado (sin gastos puntuales; dos seguros en la categoría, los dos). Con menos de 3 cargos habituales no se
 * fía del historial: si hay `importeObjetivo`, ese; si no, la mediana de lo
 * que haya.
 */
export function inferirImporteTipico(
  movimientos: readonly MovimientoHistorial[],
  importeObjetivo: IntegerAmount | null = null,
): IntegerAmount | null {
  const habituales = importesHabitualesPorMes(movimientos);
  if (habituales.length < MIN_CARGOS_IMPORTE && importeObjetivo != null) {
    return importeObjetivo;
  }
  const importes =
    habituales.length > 0
      ? habituales
      : cargosPorMes(movimientos).map(c => -c.amount);
  if (importes.length === 0) {
    return null;
  }
  return medianaRobusta(importes);
}

/**
 * Cómo se cobra: la frecuencia corregida a mano (`manual`) gana a la del
 * historial; último cargo, del historial; importe típico, del historial sin
 * gastos puntuales (con poco historial, el del objetivo por cargo).
 */
export function cobroReal(
  movimientos: readonly MovimientoHistorial[],
  manual?: MesesCobro | null,
  objetivo?: Objetivo | null | 'otro',
): CobroReal {
  const [ultimo] = cargosPorMes(movimientos, 1);
  const inferida = inferirFrecuenciaCargo(movimientos);
  const meses = manual ?? inferida;
  const importeObjetivo = importeObjetivoPorCargo(
    objetivo,
    typeof meses === 'number' ? meses : 1,
  );
  const importeTipico = inferirImporteTipico(movimientos, importeObjetivo);
  const r: CobroReal = {
    meses,
    origen: manual ? 'manual' : inferida != null ? 'historial' : null,
    importeTipico,
    ultimoMes: ultimo ? ultimo.date.slice(0, 7) : null,
  };
  if (
    importeObjetivo != null &&
    cargosHabitualesPorMes(movimientos).length < MIN_CARGOS_IMPORTE
  ) {
    r.importeDelObjetivo = true;
  }
  return r;
}

/**
 * Próximo mes de cargo visto desde `mes`: el del último cargo real si es
 * `mes`; si no, último + `cadaMeses`. Si ese ya pasó (no llegó el cargo),
 * se espera en `mes`. Sin último cargo, `mes`.
 */
export function proximoMesCargo(
  ultimoMes: string | null,
  cadaMeses: number,
  mes: string,
): string {
  if (!ultimoMes || ultimoMes >= mes || cadaMeses <= 1) {
    return mes;
  }
  const siguiente = monthUtils.addMonths(ultimoMes, cadaMeses);
  return siguiente < mes ? mes : siguiente;
}

// ---------------------------------------------------------------------------
// Pref `pagos-fijos-frecuencias`: { idCategoría: 1 | 2 | 3 | 6 | 12 }
// ---------------------------------------------------------------------------

export type FrecuenciasPagosFijos = Record<string, MesesCobro>;

/** Pref `pagos-fijos-frecuencias`, tolerante. */
export function parseFrecuenciasPagosFijos(
  raw: string | null | undefined,
): FrecuenciasPagosFijos {
  try {
    const v: unknown = raw ? JSON.parse(raw) : {};
    if (!v || typeof v !== 'object' || Array.isArray(v)) {
      return {};
    }
    const r: FrecuenciasPagosFijos = {};
    for (const [id, meses] of Object.entries(v as Record<string, unknown>)) {
      if ((MESES_COBRO as readonly unknown[]).includes(meses)) {
        r[id] = meses as MesesCobro;
      }
    }
    return r;
  } catch {
    return {};
  }
}

/** Pone (o quita, con `null`: vuelve a la del historial) la frecuencia. */
export function conFrecuencia(
  frecuencias: FrecuenciasPagosFijos,
  categoriaId: string,
  meses: MesesCobro | null,
): FrecuenciasPagosFijos {
  const r = { ...frecuencias };
  if (meses) {
    r[categoriaId] = meses;
  } else {
    delete r[categoriaId];
  }
  return r;
}

/**
 * Fecha típica de un pago anual `MM-DD`: la del cargo más grande de cada
 * año (hasta 3 años), mediana por día del año. `null` sin historial.
 */
export function inferirFechaAnual(
  movimientos: readonly MovimientoHistorial[],
): string | null {
  const porAnio = new Map<string, MovimientoHistorial>();
  for (const m of movimientos) {
    if (m.amount >= 0) {
      continue;
    }
    const anio = m.date.slice(0, 4);
    const actual = porAnio.get(anio);
    if (!actual || m.amount < actual.amount) {
      porAnio.set(anio, m);
    }
  }
  const fechas = [...porAnio.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .slice(0, 3)
    .map(([, m]) => m.date.slice(5, 10))
    .sort();
  if (fechas.length === 0) {
    return null;
  }
  return fechas[Math.floor((fechas.length - 1) / 2)];
}

/**
 * Cuenta de cargo más frecuente entre los últimos cargos (hasta 12, uno
 * por mes); a igualdad, la del cargo más reciente. `null` sin historial.
 */
export function inferirCuenta(
  movimientos: readonly MovimientoHistorial[],
): string | null {
  const cargos = cargosPorMes(movimientos).filter(c => c.account);
  const cuenta = new Map<string, number>();
  for (const c of cargos) {
    cuenta.set(c.account!, (cuenta.get(c.account!) ?? 0) + 1);
  }
  let mejor: string | null = null;
  let veces = 0;
  // Del más reciente al más antiguo: el primero que llega al máximo gana.
  for (const c of cargos) {
    const n = cuenta.get(c.account!) ?? 0;
    if (n > veces) {
      mejor = c.account;
      veces = n;
    }
  }
  return mejor;
}

// ---------------------------------------------------------------------------
// Pref `pagos-fijos-cuentas`: { idCategoría: idCuenta }
// ---------------------------------------------------------------------------

export type CuentasPagosFijos = Record<string, string>;

/** Pref `pagos-fijos-cuentas`, tolerante. */
export function parseCuentasPagosFijos(
  raw: string | null | undefined,
): CuentasPagosFijos {
  try {
    const v: unknown = raw ? JSON.parse(raw) : {};
    if (!v || typeof v !== 'object' || Array.isArray(v)) {
      return {};
    }
    const r: CuentasPagosFijos = {};
    for (const [id, cuenta] of Object.entries(v as Record<string, unknown>)) {
      if (typeof cuenta === 'string' && cuenta) {
        r[id] = cuenta;
      }
    }
    return r;
  } catch {
    return {};
  }
}

/** Pone (o quita, con `null`) la cuenta de cargo de una categoría. */
export function conCuenta(
  cuentas: CuentasPagosFijos,
  categoriaId: string,
  cuentaId: string | null,
): CuentasPagosFijos {
  const r = { ...cuentas };
  if (cuentaId) {
    r[categoriaId] = cuentaId;
  } else {
    delete r[categoriaId];
  }
  return r;
}

/** Pone varias cuentas de cargo a la vez. */
export function conCuentas(
  cuentas: CuentasPagosFijos,
  nuevas: CuentasPagosFijos,
): CuentasPagosFijos {
  return { ...cuentas, ...nuevas };
}

/**
 * Cuentas que hay que guardar solas: pagos fijos sin cuenta guardada pero
 * con una propuesta del historial («todas salen siempre de la misma
 * cuenta»). Sin historial no se guarda nada: se pide elegirla.
 */
export function cuentasPorGuardar(
  pagos: readonly Pick<PagoFijo, 'id' | 'cuentaId' | 'cuentaGuardada'>[],
): CuentasPagosFijos {
  const r: CuentasPagosFijos = {};
  for (const p of pagos) {
    if (!p.cuentaGuardada && p.cuentaId) {
      r[p.id] = p.cuentaId;
    }
  }
  return r;
}

// ---------------------------------------------------------------------------
// Cambio de cuenta de cargo: «¿A partir de ahora sale de ahí?»
// ---------------------------------------------------------------------------

export type CambioCuenta = {
  /** Id del movimiento que salió de otra cuenta. */
  movimientoId: string;
  /** Cuenta nueva (la del último cargo). */
  cuentaId: string;
  fecha: string;
};

/** Máximo de movimientos ignorados que se recuerdan. */
export const MAX_IGNORADOS = 50;

/**
 * Cargo de la categoría del mes más reciente con cargos (`mes` o el
 * anterior): el más grande del mes, como para proponer el día (así un cargo
 * pequeño suelto no hace preguntar). Si salió de una cuenta distinta de la
 * guardada y no se respondió «No» a ese movimiento, devuelve el cambio. Sin
 * cuenta guardada no se pregunta nada.
 */
export function detectarCambioCuenta({
  historial,
  guardada,
  mes,
  ignorados,
}: {
  historial: readonly MovimientoHistorial[];
  guardada: string | null;
  mes: string;
  ignorados: ReadonlySet<string>;
}): CambioCuenta | null {
  if (!guardada) {
    return null;
  }
  const anterior = monthUtils.subMonths(mes, 1);
  const recientes = historial.filter(m => {
    const mesMov = m.date.slice(0, 7);
    return m.id && m.account && (mesMov === mes || mesMov === anterior);
  });
  const [cargo] = cargosPorMes(recientes, 1);
  if (
    !cargo?.id ||
    !cargo.account ||
    cargo.account === guardada ||
    ignorados.has(cargo.id)
  ) {
    return null;
  }
  return { movimientoId: cargo.id, cuentaId: cargo.account, fecha: cargo.date };
}

/** Pref `pagos-fijos-ignorados` (JSON array de ids de movimiento), tolerante. */
export function parseIgnorados(raw: string | null | undefined): string[] {
  try {
    const v: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(v)
      ? v.filter((x): x is string => typeof x === 'string' && x !== '')
      : [];
  } catch {
    return [];
  }
}

/** Añade un movimiento ignorado; se quedan solo los `max` más recientes. */
export function conIgnorado(
  ignorados: readonly string[],
  id: string,
  max = MAX_IGNORADOS,
): string[] {
  return [...ignorados.filter(x => x !== id), id].slice(-max);
}

// ---------------------------------------------------------------------------
// Lista de pagos fijos de un mes
// ---------------------------------------------------------------------------

export type EntradaPagoFijo = {
  id: string;
  nombre: string;
  grupoId: string;
  grupoNombre: string;
  objetivo: Objetivo | null | 'otro';
  /** Salidas de la categoría en el historial (cualquier orden). */
  historial?: readonly MovimientoHistorial[];
};

/**
 * Pagos fijos de `mes` con su estado. Solo entran las categorías de
 * `fijos`; las que no tienen objetivo utilizable salen con `frecuencia`
 * e `importe` a `null` y estado `programado`.
 */
export function calcularPagosFijos({
  entradas,
  fijos,
  manuales,
  cuentas,
  frecuencias = {},
  ignorados = new Set(),
  todas = new Set(),
  datosMes,
  mes,
  hoy,
  ventanaDias = 7,
}: {
  entradas: readonly EntradaPagoFijo[];
  fijos: ReadonlySet<string>;
  /** Marcas manuales del mes (`pagadas-manuales[mes]`). */
  manuales: readonly string[];
  cuentas: CuentasPagosFijos;
  /** Frecuencias de cobro corregidas a mano (`pagos-fijos-frecuencias`). */
  frecuencias?: FrecuenciasPagosFijos;
  /** Movimientos a los que se respondió «No» al cambio de cuenta. */
  ignorados?: ReadonlySet<string>;
  /** Categorías con «Todas las cuentas». */
  todas?: ReadonlySet<string>;
  datosMes: ReadonlyMap<string, DatosCategoriaMes>;
  mes: string;
  hoy: string;
  ventanaDias?: number;
}): PagoFijo[] {
  const marcadas = new Set(manuales);
  const lista: PagoFijo[] = [];
  for (const e of entradas) {
    if (!fijos.has(e.id)) {
      continue;
    }
    const objetivo = e.objetivo === 'otro' ? null : e.objetivo;
    const frecuencia = frecuenciaDeObjetivo(objetivo);
    const historial = e.historial ?? [];
    const datos = datosMes.get(e.id);
    const pagada = esPagada({
      fijo: true,
      manual: marcadas.has(e.id),
      noPagada: marcadas.has(`${PREFIJO_NO_PAGADA}${e.id}`),
      datos,
    });

    const cobro = cobroReal(historial, frecuencias[e.id], objetivo);
    let dia: number | null = null;
    let diaInferido = false;
    let fecha: string | null = null;
    let cadaMesesCobro: number | null = null;
    /** Mes del próximo cargo (mensuales), aunque no se sepa el día. */
    let mesCargo: string | null = null;
    /** Cobro irregular: sin fecha prevista. */
    let sinFecha = false;
    let importeCargo: IntegerAmount | null =
      objetivo && frecuencia ? objetivo.importe : null;
    if (objetivo?.tipo === 'mensual') {
      dia = objetivo.dia;
      if (dia == null) {
        dia = inferirDiaCargo(historial);
        diaInferido = dia != null;
      }
      // Lo que se aparta es mensual, pero el cargo puede ser cada N meses
      // (facturas bimestrales): manda la frecuencia real. Irregular o sin
      // datos (< 3 cargos): como hasta ahora, cada mes. Irregular: sin fecha.
      cadaMesesCobro = typeof cobro.meses === 'number' ? cobro.meses : 1;
      if (cadaMesesCobro > 1) {
        importeCargo = cobro.importeTipico ?? objetivo.importe * cadaMesesCobro;
      }
      if (cobro.meses === 'irregular') {
        // Sin patrón: no hay fecha prevista (nunca «Atrasado» ni «En camino»,
        // ni previstos ni «Por hacer»). Solo cuenta el mes en que se cobró.
        importeCargo = cobro.importeTipico ?? objetivo.importe;
        mesCargo = cobro.ultimoMes === mes ? mes : null;
        fecha = null;
        sinFecha = true;
      } else {
        mesCargo = proximoMesCargo(cobro.ultimoMes, cadaMesesCobro, mes);
        fecha = dia != null ? fechaCargoDelMes(dia, mesCargo) : null;
      }
    } else if (objetivo?.tipo === 'anual' || objetivo?.tipo === 'una-vez') {
      fecha = proximoVencimiento(objetivo, `${mes}-01`);
      dia = Number(fecha.slice(8, 10));
      cadaMesesCobro = objetivo.tipo === 'anual' ? objetivo.cadaMeses : null;
    }

    // Solo cuenta este mes si el cargo cae este mes (anuales, bimestrales…).
    const delMes = sinFecha
      ? mesCargo === mes
      : (mesCargo ?? fecha?.slice(0, 7)) === mes;
    const estado = estadoPagoFijo({
      fecha: delMes ? fecha : null,
      hoy,
      pagada,
      ventanaDias,
    });

    const guardada = cuentas[e.id] ?? null;
    const todasLasCuentas = todas.has(e.id);
    lista.push({
      id: e.id,
      nombre: e.nombre,
      grupoId: e.grupoId,
      grupoNombre: e.grupoNombre,
      importe: objetivo && frecuencia ? objetivo.importe : null,
      frecuencia,
      dia,
      diaInferido,
      fecha,
      cuentaId: todasLasCuentas ? null : (guardada ?? inferirCuenta(historial)),
      cuentaGuardada: guardada != null,
      todasLasCuentas,
      cambioCuenta: todasLasCuentas
        ? null
        : detectarCambioCuenta({
            historial,
            guardada,
            mes,
            ignorados,
          }),
      estado,
      pagada,
      datos,
      cobro,
      cadaMesesCobro,
      tocaEsteMes: delMes,
      importeCargo,
    });
  }
  return lista;
}

/**
 * Pestañas de «Pagos fijos» según cómo se cobra de verdad: «Mensuales» los
 * de cada 1-3 meses (por día de cargo); «Anuales» los de cada 6-12 meses y
 * «una vez» (por fecha). Los que no tienen objetivo no entran en ninguna.
 */
export function repartirPestanas(pagos: readonly PagoFijo[]): {
  mensuales: PagoFijo[];
  anuales: PagoFijo[];
} {
  const cada = (p: PagoFijo): number | null =>
    p.cadaMesesCobro !== undefined
      ? p.cadaMesesCobro
      : p.frecuencia?.tipo === 'mensual'
        ? 1
        : p.frecuencia?.tipo === 'cada-meses'
          ? p.frecuencia.meses
          : null;
  const conObjetivo = pagos.filter(p => p.frecuencia != null);
  const mensuales = ordenarPorDia(
    conObjetivo.filter(p => {
      const n = cada(p);
      return n != null && n <= 3;
    }),
  );
  const anuales = conObjetivo
    .filter(p => {
      const n = cada(p);
      return n == null || n > 3;
    })
    .sort((a, b) => (a.fecha ?? '').localeCompare(b.fecha ?? ''));
  return { mensuales, anuales };
}

/** Mensuales ordenados por día de cargo (sin día, al final). */
export function ordenarPorDia(pagos: readonly PagoFijo[]): PagoFijo[] {
  return [...pagos].sort(
    (a, b) => (a.dia ?? 99) - (b.dia ?? 99) || a.nombre.localeCompare(b.nombre),
  );
}

export type ResumenPagosFijos = {
  /** Importe pagado este mes (suma de cargos pagados). */
  pagado: IntegerAmount;
  /** Importe total del mes (cargos de los que tocan este mes). */
  total: IntegerAmount;
  pendientes: number;
  /** Total del mes por cuenta de cargo (`''`: sin cuenta). */
  porCuenta: Map<string, IntegerAmount>;
};

/** Cabecera «Este mes: pagado X de Y» y total por cuenta (los del mes). */
export function resumirMes(
  pagos: readonly PagoFijo[],
  mes: string,
): ResumenPagosFijos {
  let pagado = 0;
  let total = 0;
  let pendientes = 0;
  const porCuenta = new Map<string, IntegerAmount>();
  for (const p of pagos) {
    // Datos sin `tocaEsteMes` (antiguos): los mensuales cuentan siempre.
    const delMes =
      p.tocaEsteMes ??
      (p.frecuencia?.tipo === 'mensual' || p.fecha?.slice(0, 7) === mes);
    const importe = p.importeCargo ?? p.importe;
    if (!delMes || importe == null) {
      continue;
    }
    total += importe;
    if (p.pagada) {
      pagado += importe;
    } else {
      pendientes += 1;
    }
    const cuenta = p.cuentaId ?? '';
    porCuenta.set(cuenta, (porCuenta.get(cuenta) ?? 0) + importe);
  }
  return { pagado, total, pendientes, porCuenta };
}

/** Primer mes del historial que se consulta (12 meses para mensuales, 36 para anuales). */
export function desdeHistorial(mes: string, meses = 36): string {
  return monthUtils.subMonths(mes, meses);
}
